
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface ConversationItemProps {
  conversation: any;
  currentUserId: string;
  onSelect: (conversation: any) => void;
  id?: string;
  isHighlighted?: boolean;
}

const ConversationItem = ({ 
  conversation, 
  currentUserId, 
  onSelect,
  id,
  isHighlighted = false
}: ConversationItemProps) => {
  const isUserBusiness = conversation.member_id !== currentUserId;
  
  // Determine counterpart (who the user is talking to)
  const counterpart = isUserBusiness 
    ? conversation.member
    : conversation.business;
  
  // Use username for community members, business name for businesses
  const counterpartName = isUserBusiness
    ? counterpart?.username || counterpart?.email || 'User'
    : counterpart?.name || 'Business';
    
  const counterpartImage = isUserBusiness
    ? counterpart?.avatar_url
    : counterpart?.company_logo;
    
  // Format last activity time
  const lastActivityTime = conversation.last_message_at
    ? formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })
    : '';
    
  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Card 
      id={id}
      className={cn(
        "cursor-pointer hover:shadow-md transition-shadow",
        isHighlighted && "ring-2 ring-primary"
      )} 
      onClick={() => onSelect(conversation)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
            <AvatarImage src={counterpartImage || undefined} alt={counterpartName} />
            <AvatarFallback>{getInitials(counterpartName)}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-lg truncate">{counterpartName}</h3>
              {conversation.unreadCount > 0 && (
                <Badge variant="default" className="ml-2">
                  {conversation.unreadCount}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center justify-between text-sm text-muted-foreground mt-1">
              <p className="truncate max-w-[200px]">
                {conversation.lastMessage?.content || 'No messages yet'}
              </p>
              <span className="text-xs whitespace-nowrap ml-2">
                {lastActivityTime}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConversationItem;
